import { useLazyQuery } from '@apollo/client';
import { Select, Spin } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DYNAMIC_CONFIG_TYPES,
  TAGS_CONFIG_SUBTYPES,
} from '../../../../../common/constants';
import { DYNAMIC_CONFIG_ITEMS } from '../../graphql/Queries';

const { Option } = Select;

const TAGS_LIMIT = 10;
const LOADING_OPTION_KEY = '__loading__';
const PRELOAD_LIMIT = 200;

const ReservationUserTagSelect = ({
  value = [],
  onChange,
  placeholder = 'Select Tags',
  disabled = false,
  preloadTagIds = [],
}) => {
  const [options, setOptions] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const skipRef = useRef(0);
  const searchRef = useRef('');
  const requestIdRef = useRef(0);
  const labelMapRef = useRef({});
  const listRef = useRef(null);
  const preloadedIdsRef = useRef(new Set());

  const [fetchTags] = useLazyQuery(DYNAMIC_CONFIG_ITEMS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const buildOptions = (rows) =>
    (rows || [])?.map((tag) => ({
      label: tag?.itemKey,
      emoji: tag?.value || '',
      value: tag?.id,
    }));

  const loadTags = useCallback(
    async (skip = 0, searchVal = '') => {
      const requestId = requestIdRef.current;
      try {
        const result = await fetchTags({
          variables: {
            filter: {
              configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
              configSubtypes: [TAGS_CONFIG_SUBTYPES?.USERS?.value],
              skip,
              limit: TAGS_LIMIT,
              search: searchVal || null,
              isActive: true,
            },
            sort: { field: 'createdAt', order: 'DESC' },
          },
        });

        if (requestId !== requestIdRef.current) return;

        const rows = result?.data?.dynamicConfigItems?.data || [];
        const total = result?.data?.dynamicConfigItems?.count || 0;
        const mapped = buildOptions(rows);
        mapped?.forEach((option) => {
          labelMapRef.current[option?.value] = option;
        });

        if (skip === 0) {
          setOptions((prev) => {
            const preloadedExtras = prev?.filter(
              (option) =>
                preloadedIdsRef.current?.has(option?.value) &&
                !mapped?.some((item) => item?.value === option?.value),
            );
            return [...mapped, ...preloadedExtras];
          });
        } else {
          setOptions((prev) => {
            const existingIds = new Set(prev?.map((option) => option?.value));
            return [
              ...prev,
              ...mapped?.filter((option) => !existingIds?.has(option?.value)),
            ];
          });
        }
        setHasMore(skip + rows?.length < total);
      } catch (error) {
        if (requestId === requestIdRef.current) setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          if (skip === 0) setIsInitialLoading(false);
          else setIsLoadingMore(false);
        }
      }
    },
    [fetchTags],
  );

  useEffect(() => {
    skipRef.current = 0;
    setOptions([]);
    setHasMore(true);
    setIsInitialLoading(true);
    loadTags(0, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const missingIds = (preloadTagIds || [])?.filter(
      (id) => id && !labelMapRef.current?.[id],
    );
    if (!missingIds?.length) return;

    fetchTags({
      variables: {
        filter: {
          configType: DYNAMIC_CONFIG_TYPES?.TAGS?.value,
          configSubtypes: [TAGS_CONFIG_SUBTYPES?.USERS?.value],
          skip: 0,
          limit: PRELOAD_LIMIT,
        },
        sort: { field: 'createdAt', order: 'DESC' },
      },
    })
      .then((result) => {
        const mapped = buildOptions(result?.data?.dynamicConfigItems?.data)?.filter(
          (option) => missingIds?.includes(option?.value),
        );
        if (!mapped?.length) return;
        mapped?.forEach((option) => {
          labelMapRef.current[option?.value] = option;
          preloadedIdsRef.current?.add(option?.value);
        });
        setOptions((prev) => {
          const existingIds = new Set(prev?.map((option) => option?.value));
          return [
            ...mapped?.filter((option) => !existingIds?.has(option?.value)),
            ...prev,
          ];
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(preloadTagIds)]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLoadingMore || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isLoadingMore]);

  const handleSearch = useCallback(
    debounce((searchValue) => {
      requestIdRef.current += 1;
      skipRef.current = 0;
      searchRef.current = searchValue;
      setOptions([]);
      setHasMore(true);
      setIsInitialLoading(true);
      loadTags(0, searchValue);
    }, 500),
    [loadTags],
  );

  useEffect(() => () => handleSearch?.cancel?.(), [handleSearch]);

  const handlePopupScroll = (event) => {
    const { target } = event;
    listRef.current = target;
    if (
      !isLoadingMore &&
      hasMore &&
      target?.scrollTop + target?.offsetHeight >= target?.scrollHeight - 20
    ) {
      const nextSkip = skipRef.current + TAGS_LIMIT;
      skipRef.current = nextSkip;
      setIsLoadingMore(true);
      loadTags(nextSkip, searchRef.current);
    }
  };

  let notFoundContent = 'No tags found';
  if (isInitialLoading) {
    notFoundContent = <Spin size="small" />;
  }

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={isInitialLoading && options?.length === 0}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      filterOption={false}
      optionLabelProp="label"
      notFoundContent={notFoundContent}
    >
      {options?.map((option) => (
        <Option key={option?.value} value={option?.value} label={option?.label}>
          <span className="d-flex align-center">
            {option?.emoji && <span className="mr-8">{option?.emoji}</span>}
            <span>{option?.label}</span>
          </span>
        </Option>
      ))}
      {isLoadingMore && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </Select>
  );
};

export default ReservationUserTagSelect;
